import React from 'react';
import moment from 'moment';
import DropdownMenu from '../../components/dropdown-menu';
import {
  DrawerFacts,
  DrawerField as Field,
  DrawerItems,
  DrawerSection as Section,
  DrawerShell,
} from '../../components/detail-drawer';
import '../../components/data-grid/data-grid.css';
import { COLORS, formatWeight } from './truck-assignments.utils';

/**
 * Fiche d'une commande des affectations (bouton « œil ») : camion, séquence,
 * ETA, réceptionnaire et, pour chaque BL de l'étape, ses articles. L'adresse
 * complète et les téléphones arrivent avec GET /tours-planning/:id/details.
 */
export default function AssignmentDrawer({
  delivery,
  truck,
  details,
  isLoadingDetails,
  menuItems,
  onClose,
}) {
  const step = delivery.raw;
  const deliveryDetails = step?.delivery_details || [];
  const address = details?.address;
  const phones = address?.phone_numbers || [];
  const isAssigned = Boolean(delivery.vehicleId);
  const loadingHint = isLoadingDetails ? 'Chargement…' : null;

  return (
    <DrawerShell ariaLabel={`Commande ${delivery.client}`} onClose={onClose}>
      <div className="agv-drawer__header">
        <div className="agv-drawer__title-row">
          <button
            type="button"
            className="agv-dg-icon-btn agv-dg-icon-btn--large"
            onClick={onClose}
            title="Retour à la liste"
          >
            ‹
          </button>
          <span className="agv-drawer__title">{delivery.client}</span>
          <span className="agv-dg-status agv-dg-status--strong">
            <span
              className="agv-dg-dot"
              style={{
                backgroundColor: isAssigned
                  ? truck?.color || COLORS.primary
                  : COLORS.unassigned,
              }}
            />
            {isAssigned ? `Affectée · ${truck?.key || ''}` : 'À placer'}
          </span>
          <span className="agv-dg-spacer" />
          <DropdownMenu
            width={300}
            items={menuItems}
            renderTrigger={({ toggle }) => (
              <button
                type="button"
                className="agv-dg-ghost-btn agv-dg-ghost-btn--square"
                onClick={toggle}
                title="Actions"
              >
                ···
              </button>
            )}
          />
          <button
            type="button"
            className="agv-dg-icon-btn"
            onClick={onClose}
            title="Fermer"
          >
            ✕
          </button>
        </div>
        <span className="agv-drawer__origin">
          {deliveryDetails.length} BL pour ce réceptionnaire
          {step?.created_at &&
            ` · reçue le ${moment(step.created_at).format('DD/MM/YYYY HH:mm')}`}
        </span>
      </div>

      <DrawerFacts
        facts={[
          {
            label: 'Camion',
            value: isAssigned ? truck?.key || '—' : 'À placer',
            hint: isAssigned
              ? [
                  truck?.licensePlate,
                  delivery.order && `arrêt n° ${delivery.order}`,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : null,
          },
          {
            label: 'ETA',
            value: isAssigned
              ? `${delivery.etaTime || '—'}${
                  delivery.etaDayOffset > 0 ? ` J+${delivery.etaDayOffset}` : ''
                }`
              : '—',
            hint:
              delivery.etaExceedsTimeWindow || delivery.isOutOfTimeWindow
                ? 'hors horaires du camion'
                : null,
            className:
              delivery.etaExceedsTimeWindow || delivery.isOutOfTimeWindow
                ? 'agv-dg-tone--danger'
                : '',
          },
          { label: 'Poids', value: formatWeight(delivery.weight) },
          { label: 'BL', value: deliveryDetails.length || delivery.blCount },
        ]}
      />

      <div className="agv-drawer__body">
        <Section title="RÉCEPTIONNAIRE & LIVRAISON">
          <Field
            label="Réceptionnaire"
            value={delivery.client}
            valueClassName="agv-drawer-field__value--strong"
          />
          <Field
            label="Adresse de livraison"
            value={delivery.address}
            hint={
              loadingHint ||
              (delivery.coordinates
                ? `● géocodée · ${delivery.coordinates.lat.toFixed(
                    3
                  )}, ${delivery.coordinates.lng.toFixed(3)}`
                : 'adresse non géocodée')
            }
          />
          <Field
            label="Téléphones"
            value={
              phones.length > 0
                ? phones
                    .map((phone) =>
                      [phone.phone_number, phone.name && `(${phone.name})`]
                        .filter(Boolean)
                        .join(' ')
                    )
                    .join(' · ')
                : '—'
            }
            hint={loadingHint}
          />
        </Section>

        {deliveryDetails.map((detail, index) => {
          const items = detail.items || [];
          const weight = items.reduce(
            (sum, item) =>
              sum +
              (parseFloat(item.weight) || 0) *
                (parseInt(item.quantity, 10) || 1),
            0
          );
          return (
            <Section
              key={detail.id || index}
              title={`BL ${detail.delivery_key || detail.bl_key || '—'} · ${
                items.length
              } ARTICLE${items.length > 1 ? 'S' : ''}`}
              action={
                <span className="agv-drawer__section-meta">
                  total {formatWeight(weight)}
                </span>
              }
            >
              <DrawerItems items={items} />
            </Section>
          );
        })}

        {details?.step?.comments && (
          <Section title="COMMENTAIRE DE COMMANDE  ·  lecture seule">
            <div className="agv-drawer-comment">
              <span>💬</span>
              <span>{details.step.comments}</span>
            </div>
          </Section>
        )}
      </div>

      <div className="agv-drawer__footer">
        <span className="agv-drawer__footer-note">
          Lecture seule. Les modifications faites dans le TMS ne sont jamais
          répercutées vers l&apos;ERP.
        </span>
        <button type="button" className="agv-dg-ghost-btn" onClick={onClose}>
          Fermer
        </button>
      </div>
    </DrawerShell>
  );
}
